/* eslint-disable camelcase */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Button, Typography, Tooltip, Chip, alpha,
    Snackbar, Alert,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Iconify from 'src/components/Iconify';
import { common } from 'src/theme/palette';
import { callApi } from 'src/utils/apiCall';
import publicApiCall from 'src/utils/publicApiCall';
import { generateConsentPdfBlob } from 'src/utils/consentPdfExport';
import ConsentFormRenderer, { validateForm } from 'src/pages/public/components/ConsentFormRenderer';
import ConsentSubmitSuccess from 'src/pages/public/components/ConsentSubmitSuccess';
import StepProgress from 'src/pages/public/components/StepProgress';
import StepNavigation from 'src/pages/public/components/StepNavigation';
import { validateStep } from 'src/pages/public/components/StepValidation';
import PatientInfoCard from 'src/pages/public/components/PatientInfoCard';
import ConsentLoader from './shared/ConsentLoader';

// ── API → Internal format transformation ────────────────────────────────────
// The API returns sections with `description` as a JSON string containing
// `frontend_type`, and fields with `metadata` containing `frontend_type`,
// `placeholder`, `lead_mapping`, `content`, `options_full`, etc.
// The ConsentFormRenderer/ConsentFieldRenderer expect `section.section_type`,
// `field.type`, `field.placeholder`, `field.required`, `field.lead_mapping`,
// `field.default_value`, `field.options`, etc. at the top level.

// EXPORTED: also reused by SendFormsDialog/FormQuickPreviewDrawer so the
// wizard can render through the real ConsentFormRenderer (Track 2026-04-26
// — "use the actual form renderer in the wizard preview"). Keeping the
// helpers here keeps the canonical source-of-truth in one place — refactor
// to a shared utils module if a third consumer appears.
export function parseSectionDescription(description) {
    if (!description) return {};
    try {
        return JSON.parse(description);
    } catch {
        return {};
    }
}

export function transformField(apiField) {
    const meta = apiField.metadata || {};
    const frontendType = meta.frontend_type || 'text';

    // Build options from options_full (for checkbox_group, radio, select)
    let options = null;
    if (meta.options_full && Array.isArray(meta.options_full)) {
        options = meta.options_full;
    } else if (apiField.choices && Array.isArray(apiField.choices) && apiField.choices.length > 0) {
        // Fall back to choices array — each choice might be a string or an object
        options = apiField.choices.map((c) =>
            typeof c === 'string' ? { label: c, value: c } : c
        );
    }

    // Determine default_value — for paragraph_display, it's in metadata.content
    // For html fields, content is in long_text or label (legacy)
    let defaultValue = null;
    if (frontendType === 'html') {
        defaultValue = apiField.long_text || apiField.label || null;
    } else if (frontendType === 'paragraph_display' && meta.content) {
        defaultValue = meta.content;
    } else if (meta.image_url) {
        defaultValue = meta.image_url;
    }

    // For HTML fields: use display_label as label, content goes in default_value
    const isHtmlField = frontendType === 'html';
    const label = isHtmlField ? (meta.display_label || '') : apiField.label;

    return {
        ...apiField,
        // Top-level fields the renderer expects
        type: frontendType,
        label,
        placeholder: meta.placeholder || '',
        required: apiField.is_required ?? false,
        lead_mapping: meta.lead_mapping || null,
        default_value: defaultValue,
        helper_text: meta.helper_text || '',
        options,
        // Keep metadata for signature pad and other special fields
        metadata: meta,
        // For HTML fields, preserve long_text for normalizeField fallback
        ...(isHtmlField && apiField.long_text && { long_text: apiField.long_text }),
        // Column span info for grid layouts
        columnSpan: meta.style?.columnSpan || 12,
    };
}

export function transformSection(apiSection) {
    const descMeta = parseSectionDescription(apiSection.description);
    const sectionType = descMeta.frontend_type || 'custom';

    return {
        ...apiSection,
        section_type: sectionType,
        // Preserve the builder's frontend_id for step_group matching
        frontend_id: descMeta.frontend_id || null,
        // Replace the raw JSON description with the human-readable text (if any)
        description: descMeta.text || '',
        // Section-level condition from description meta
        condition: descMeta.condition || null,
        // Section styling from description meta
        style: descMeta.style || {},
        // Transform all fields
        fields: (apiSection.fields || []).map(transformField),
    };
}

export function transformFormData(apiFormData) {
    if (!apiFormData) return apiFormData;
    return {
        ...apiFormData,
        sections: (apiFormData.sections || []).map(transformSection),
    };
}

// ── Lead data normalization ─────────────────────────────────────────────────
// The lead mapping paths (e.g., 'first_name', 'email', 'phone_number',
// 'date_of_birth', 'address') need to resolve against the real lead API
// response, where some data is nested (lead_contact.email, lead_contact.phone).
// This function flattens the lead into a shape the mappings expect.

function normalizeLead(rawLead) {
    if (!rawLead) return null;

    const contact = rawLead.lead_contact || {};
    const addr = rawLead.lead_address || {};
    const treatments = rawLead.lead_treatments || [];

    // Parse DD-MM-YYYY date format to YYYY-MM-DD for JS Date compatibility
    let dob = rawLead.dob || rawLead.date_of_birth || '';
    if (dob && /^\d{2}-\d{2}-\d{4}/.test(dob)) {
        const [dd, mm, yyyy] = dob.split('-');
        dob = `${yyyy}-${mm}-${dd}`;
    }

    // Build a flattened lead object for mapping resolution
    return {
        ...rawLead,
        // Flatten common contact fields to top level
        email: rawLead.email || contact.email || '',
        phone_number: rawLead.phone_number || contact.phone || '',
        phone: rawLead.phone || contact.phone || '',
        // Fix DOB format
        date_of_birth: dob,
        // Flatten address fields (lead_address is an object, not a string)
        address: typeof rawLead.address === 'string'
            ? rawLead.address
            : [addr.address_line_1, addr.address_line_2].filter(Boolean).join(', ') || '',
        city: rawLead.city || addr.city || '',
        postcode: rawLead.postcode || addr.postcode || '',
        // Normalize salutation to lowercase for Select matching
        salutation: (rawLead.salutation || '').toLowerCase(),
        // Ensure lead_treatments keeps its structure for nested access
        lead_treatments: treatments.map((lt) => ({
            ...lt,
            // Normalize treatment_item: API may return treatment as string or
            // with treatment_data wrapper
            treatment_item: lt.treatment_item
                || lt.treatment_data?.treatment_item
                || (typeof lt.treatment === 'string'
                    ? { name: lt.treatment }
                    : lt.treatment)
                || {},
        })),
    };
}

// ── Resolve lead mapping value from lead object ─────────────────────────────

function getNestedValue(obj, path) {
    if (!obj || !path) return null;
    return path.split('.').reduce((acc, key) => {
        if (acc === null || acc === undefined) return null;
        if (Array.isArray(acc)) return acc[0]?.[key];
        return acc[key];
    }, obj);
}

function applyLeadMappings(sections, leadData) {
    const values = {};
    if (!leadData) return values;
    sections.forEach((section) => {
        (section.fields || []).forEach((field) => {
            const rawMapping = field.lead_mapping;
            // Sanitize: ignore Python "None"/"null" strings
            const mapping = (rawMapping && rawMapping !== 'None' && rawMapping !== 'null') ? rawMapping : null;
            if (!mapping) return;
            const mapped = getNestedValue(leadData, mapping);
            if (mapped !== null && mapped !== undefined && mapped !== '') {
                values[field.id] = String(mapped);
            }
        });
    });
    return values;
}

// ── Extract form metadata for advanced mode detection ─────────────────────
export function extractFormMetadata(rawFormData) {
    if (!rawFormData?.description) return { theme: null, stepGroups: [], formMode: 'simple', userDescription: '' };
    try {
        const parsed = JSON.parse(rawFormData.description);
        return {
            theme: parsed.theme || null,
            stepGroups: parsed.step_groups || [],
            formMode: parsed.form_mode || 'simple',
            userDescription: parsed.user_description || '',
        };
    } catch {
        return { theme: null, stepGroups: [], formMode: 'simple', userDescription: '' };
    }
}

// ── Component ──

const ConsentFormPreview = ({ data, onClose }) => {
    const { formData: rawFormData, leadData: rawLeadData, isDummy, practiceId, formId } = data;

    // Transform API data to internal format
    const formData = useMemo(() => transformFormData(rawFormData), [rawFormData]);
    const leadData = useMemo(() => normalizeLead(rawLeadData), [rawLeadData]);

    const sections = formData?.sections || [];
    const conditionalLogics = formData?.conditional_logics || [];

    // Parse theme + brand info + user description + step groups from description JSON
    const formMeta = useMemo(() => extractFormMetadata(rawFormData), [rawFormData]);

    const formTheme = formMeta.theme;
    const formUserDescription = formMeta.userDescription;
    const rawStepGroups = formMeta.stepGroups;

    // Remap step_group section_ids from builder frontend_ids to API section.id.
    // The builder stores UUID-based frontend_ids in step_groups, but the API
    // returns integer database IDs. We build a frontend_id → section.id map
    // and translate so ConsentFormRenderer's sectionIds filter works.
    const stepGroups = useMemo(() => {
        if (!rawStepGroups.length || !sections.length) return rawStepGroups;
        // Build mapping: frontend_id → String(section.id)
        const frontendToApiId = {};
        sections.forEach((s) => {
            if (s.frontend_id) {
                frontendToApiId[s.frontend_id] = s.id;
            }
        });
        // If no frontend_ids exist, section_ids might already be API IDs — return as-is
        if (Object.keys(frontendToApiId).length === 0) return rawStepGroups;

        return rawStepGroups.map((group) => ({
            ...group,
            section_ids: (group.section_ids || []).map(
                (sid) => frontendToApiId[sid] || sid
            ),
        }));
    }, [rawStepGroups, sections]);

    const isAdvancedMode = formMeta.formMode === 'advanced' && stepGroups.length > 0;

    // Build brandInfo from the brand_image section's image field
    const brandInfo = useMemo(() => {
        const brandSection = sections.find((s) => s.section_type === 'brand_image');
        if (!brandSection) return null;
        const imageField = (brandSection.fields || []).find((f) => f.type === 'image');
        const logoUrl = imageField?.default_value || imageField?.metadata?.image_url || '';
        return {
            logoUrl: logoUrl.replace('media:', ''),
            practiceName: formTheme?.practiceName || rawFormData?.name || '',
            primaryColor: formTheme?.primaryColor || '#00AB55',
        };
    }, [sections, formTheme, rawFormData]);

    // Enrich theme with practice name and form name for system auto-fill
    const enrichedTheme = useMemo(() => {
        if (!formTheme) return null;
        return {
            ...formTheme,
            practiceName: brandInfo?.practiceName || rawFormData?.practice_name || rawFormData?.name || '',
            formName: rawFormData?.name || '',
        };
    }, [formTheme, brandInfo, rawFormData]);

    // Layout mode: card vs flat — must match the builder preview + public page behavior
    const layoutMode = enrichedTheme?.layoutMode || 'card';
    const isCardLayout = layoutMode === 'card';
    const outerBg = (enrichedTheme?.outerBackgroundColor || '#f4f6f8').toLowerCase();
    const pageBg = (enrichedTheme?.pageBackgroundColor || '#ffffff').toLowerCase();
    const bgMatch = outerBg === pageBg;

    // Pre-fill from lead mappings
    const initialValues = useMemo(
        () => applyLeadMappings(sections, leadData),
        [sections, leadData]
    );

    const [fieldValues, setFieldValues] = useState(initialValues);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const formRef = useRef(null);

    // ── Step navigation state (advanced mode) ──
    const [currentStep, setCurrentStep] = useState(0);
    const totalSteps = stepGroups.length;
    const currentStepGroup = stepGroups[currentStep] || {};
    const currentSectionIds = currentStepGroup.section_ids || [];

    const handleStepBack = useCallback(() => {
        setCurrentStep((prev) => Math.max(0, prev - 1));
        setErrors({});
    }, []);

    const handleStepContinue = useCallback(() => {
        if (!isAdvancedMode) return;
        // Validate current step sections (respects conditional visibility)
        const stepSections = sections.filter((s) => currentSectionIds.includes(s.id));
        const { valid, errors: stepErrors } = validateStep(stepSections, fieldValues, [], null);
        if (!valid) {
            setErrors(stepErrors);
            const firstErrorId = Object.keys(stepErrors)[0];
            const el = document.querySelector(`[data-field-id="${firstErrorId}"]`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        setErrors({});
        if (currentStep < totalSteps - 1) {
            setCurrentStep((prev) => prev + 1);
        }
    }, [isAdvancedMode, sections, currentSectionIds, fieldValues, currentStep, totalSteps]);

    const handleStepClick = useCallback((idx) => {
        if (idx < currentStep) {
            setCurrentStep(idx);
            setErrors({});
        }
    }, [currentStep]);

    // Remaining toggle_ack fields for current step
    const currentStepSections = isAdvancedMode
        ? sections.filter((s) => currentSectionIds.includes(s.id))
        : [];
    const remainingAcks = currentStepSections
        .flatMap((s) => (s.fields || []).filter(
            (f) => (f.metadata?.frontend_type || f.type) === 'toggle_ack' && f.required
        ))
        .filter((f) => !fieldValues[f.id]).length;

    const handleFieldChange = useCallback((fieldId, value) => {
        setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
        setErrors((prev) => {
            if (!prev[fieldId]) return prev;
            const next = { ...prev };
            delete next[fieldId];
            return next;
        });
    }, []);

    // Display-only field types to exclude from submission
    const DISPLAY_ONLY_TYPES = ['image', 'heading', 'paragraph_display', 'divider', 'html'];

    const filterSubmittableValues = (values, secs) => {
        const displayFieldIds = new Set();
        (secs || []).forEach((sec) => {
            (sec.fields || []).forEach((field) => {
                const ft = field.type || field.metadata?.frontend_type || '';
                if (DISPLAY_ONLY_TYPES.includes(ft)) {
                    displayFieldIds.add(String(field.id));
                }
            });
        });
        const filtered = {};
        Object.entries(values).forEach(([key, val]) => {
            if (!displayFieldIds.has(String(key))) {
                filtered[key] = val;
            }
        });
        return filtered;
    };

    const handleSubmit = async () => {
        if (isDummy) return; // Safety net — button should be disabled

        const validationErrors = validateForm(sections, fieldValues, conditionalLogics, leadData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            const firstErrorId = Object.keys(validationErrors)[0];
            const el = document.querySelector(`[data-field-id="${firstErrorId}"]`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        setIsSubmitting(true);
        setErrors({});

        try {
            // Step 1: Create a consent-request to get an access_token
            const crResponse = await callApi(
                `/api/v1/practices/${practiceId}/consent-requests/`,
                'post',
                { form: formId, lead: leadData?.id, expires_in_hours: 24, channel: 'PREVIEW' }
            );

            if (!crResponse.ok && crResponse.status !== 201) {
                setIsSubmitting(false);
                setSnackbar({
                    open: true,
                    message: crResponse.data?.detail || 'Failed to create consent request. Please try again.',
                    severity: 'error',
                });
                return;
            }

            const accessToken = crResponse.data?.access_token;

            if (!accessToken) {
                // Fallback: submit directly if consent-request creation didn't return a token
                const directPayload = {
                    field_values: filterSubmittableValues(fieldValues, sections),
                    metadata: {
                        lead_id: leadData?.id,
                        submitted_at: new Date().toISOString(),
                        user_agent: navigator.userAgent,
                        preview_submission: true,
                    },
                };
                const directRes = await callApi(
                    `/api/v1/practices/${practiceId}/forms/${formId}/submissions/`,
                    'post',
                    directPayload
                );
                setIsSubmitting(false);
                if (directRes.status === 201 || directRes.ok) {
                    setIsSubmitted(true);
                } else {
                    setSnackbar({ open: true, message: directRes.data?.detail || 'Submission failed.', severity: 'error' });
                }
                return;
            }

            // Step 2: Generate PDF blob from form DOM
            let pdfBlob = null;
            if (formRef.current) {
                try {
                    pdfBlob = await generateConsentPdfBlob(formRef.current, 'consent_form_signed');
                } catch {
                    // PDF generation failed — continue without PDF
                }
            }

            // Step 3: Submit via the public consent endpoint (same as patient page)
            const submittableValues = filterSubmittableValues(fieldValues, sections);
            const buildFormPayload = (pdf) => {
                const fd = new FormData();
                fd.append('field_values', JSON.stringify(submittableValues));
                fd.append('metadata', JSON.stringify({
                    submitted_at: new Date().toISOString(),
                    user_agent: navigator.userAgent,
                    preview_submission: true,
                }));
                if (pdf) {
                    fd.append('pdf_file', pdf, 'consent_form_signed.pdf');
                }
                return fd;
            };

            let response = await publicApiCall(
                `/api/v1/public/consent/${accessToken}/submit/`,
                'post',
                buildFormPayload(pdfBlob)
            );

            // Retry without PDF if it failed
            if (!response.ok && pdfBlob && response.status !== 409 && response.status !== 410) {
                response = await publicApiCall(
                    `/api/v1/public/consent/${accessToken}/submit/`,
                    'post',
                    buildFormPayload(null)
                );
            }

            setIsSubmitting(false);

            if (response.ok) {
                setIsSubmitted(true);
            } else if (response.status === 409) {
                setIsSubmitted(true);
                setSnackbar({ open: true, message: 'This form has already been submitted.', severity: 'info' });
            } else {
                setSnackbar({
                    open: true,
                    message: response.data?.detail || 'Submission failed. Please try again.',
                    severity: 'error',
                });
            }
        } catch (err) {
            setIsSubmitting(false);
            setSnackbar({
                open: true,
                message: err.message || 'Submission failed. Please try again.',
                severity: 'error',
            });
        }
    };

    // ── Sticky top bar (shared between states) ──
    const TopBar = ({ children }) => (
        <Box sx={{
            bgcolor: '#fff', borderBottom: '1px solid', borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5,
            flexShrink: 0,
        }}>
            <Button
                startIcon={<Iconify icon="eva:close-fill" width={20} />}
                onClick={onClose}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
            >
                Close Preview
            </Button>
            {children}
        </Box>
    );

    TopBar.propTypes = {
        children: PropTypes.node,
    };

    // ── Mode badge ──
    const ModeBadge = () => isDummy ? (
        <Chip
            icon={<Iconify icon="solar:test-tube-bold" width={14} />}
            label="Preview Mode — Submissions Disabled"
            size="small"
            sx={{
                height: 26, fontWeight: 600, fontSize: '11px',
                bgcolor: alpha('#855CF8', 0.08), color: '#855CF8',
                borderRadius: '6px',
                '& .MuiChip-icon': { color: '#855CF8' },
            }}
        />
    ) : (
        <Chip
            icon={<Iconify icon="solar:user-check-bold" width={14} />}
            label={`Live — ${leadData?.first_name || ''} ${leadData?.last_name || ''}`.trim()}
            size="small"
            sx={{
                height: 26, fontWeight: 600, fontSize: '11px',
                bgcolor: alpha(common.mintyZest, 0.08), color: common.mintyZest,
                borderRadius: '6px',
                '& .MuiChip-icon': { color: common.mintyZest },
            }}
        />
    );

    // ── Success state ──
    if (isSubmitted) {
        const patientName = fieldValues[
            sections
                .flatMap((s) => s.fields || [])
                .find((f) => f.lead_mapping === 'first_name')?.id
        ] || leadData?.first_name || '';

        return (
            <Box sx={{ position: 'fixed', inset: 0, zIndex: 1400, bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
                <TopBar />
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ConsentSubmitSuccess patientName={patientName} />
                </Box>
            </Box>
        );
    }

    // ── Main render ──
    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1400, bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
            <TopBar>
                <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                        {formData?.name || 'Consent Form'}
                    </Typography>
                    {formUserDescription && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {formUserDescription}
                        </Typography>
                    )}
                </Box>
                <ModeBadge />
            </TopBar>

            {/* Scrollable form body */}
            <Box sx={{
                flex: 1, overflowY: 'auto',
                py: { xs: 2, sm: isCardLayout ? 5 : 2 }, px: { xs: 2, sm: isCardLayout ? 4 : 2 },
                bgcolor: enrichedTheme?.outerBackgroundColor || '#f4f6f8',
            }}>
                {/* Page wrapper — matches the public consent page + builder preview layout */}
                <Box sx={{
                    maxWidth: enrichedTheme?.pageMaxWidth ? `${enrichedTheme.pageMaxWidth}px` : 720,
                    width: enrichedTheme?.pageWidthPercent ? `${enrichedTheme.pageWidthPercent}%` : '100%',
                    mx: 'auto',
                    bgcolor: enrichedTheme?.pageBackgroundColor || '#ffffff',
                    py: { xs: `${Math.min(enrichedTheme?.pagePadding ?? 32, 16)}px`, sm: `${enrichedTheme?.pagePadding || 32}px` },
                    px: { xs: `${Math.min(enrichedTheme?.pagePadding ?? 32, 16)}px`, sm: `${enrichedTheme?.pagePadding || 32}px` },
                    borderRadius: isCardLayout && !bgMatch ? { xs: 0, sm: '12px' } : 0,
                    boxShadow: isCardLayout && !bgMatch
                        ? { xs: 'none', sm: '0 0 2px rgba(145,158,171,0.24), 0 16px 32px -4px rgba(145,158,171,0.24)' }
                        : 'none',
                    fontFamily: enrichedTheme?.fontFamily || 'inherit',
                }}>
                    {/* Patient info card */}
                    {leadData && !isDummy && (
                        <PatientInfoCard
                            leadData={leadData}
                            practiceName={enrichedTheme?.practiceName || brandInfo?.practiceName || ''}
                        />
                    )}

                    {/* Step progress (advanced mode) */}
                    {isAdvancedMode && (
                        <StepProgress
                            steps={stepGroups}
                            currentStep={currentStep}
                            onStepClick={handleStepClick}
                        />
                    )}

                    {/* Form renderer */}
                    <Box
                        ref={formRef}
                        key={isAdvancedMode ? currentStep : 'all'}
                        sx={isAdvancedMode ? {
                            '@keyframes previewStepFade': {
                                from: { opacity: 0, transform: 'translateY(8px)' },
                                to: { opacity: 1, transform: 'translateY(0)' },
                            },
                            animation: 'previewStepFade 0.25s ease',
                        } : {}}
                    >
                        <ConsentFormRenderer
                            sections={sections}
                            sectionIds={isAdvancedMode ? currentSectionIds : null}
                            fieldValues={fieldValues}
                            onChange={handleFieldChange}
                            errors={errors}
                            conditionalLogics={conditionalLogics}
                            leadData={leadData}
                            brandInfo={brandInfo}
                            theme={enrichedTheme}
                            readOnly={false}
                        />
                    </Box>

                    {/* Step navigation (advanced mode) */}
                    {isAdvancedMode && (() => {
                        const noOp = () => {};
                        const isLast = currentStep === totalSteps - 1;
                        let nextHandler = handleStepContinue;
                        if (isLast) {
                            nextHandler = isDummy ? noOp : handleSubmit;
                        }
                        return (
                        <StepNavigation
                            onBack={handleStepBack}
                            onNext={nextHandler}
                            onSubmit={isDummy ? noOp : handleSubmit}
                            isFirstStep={currentStep === 0}
                            isLastStep={currentStep === totalSteps - 1}
                            remainingAcks={remainingAcks}
                            isSubmitting={isSubmitting}
                            theme={enrichedTheme}
                        />
                        );
                    })()}

                    {/* Submit area (simple mode only) */}
                    {!isAdvancedMode && (
                        <Box sx={{ textAlign: 'center', mt: 5, mb: 6 }}>
                            {isDummy ? (
                                <Tooltip title="Submissions are disabled in preview mode" arrow>
                                    <span>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            disabled
                                            sx={{
                                                borderRadius: enrichedTheme?.submitButtonBorderRadius || '12px',
                                                textTransform: 'none', fontWeight: 700,
                                                px: 6, py: 1.75,
                                                fontSize: '16px',
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            {enrichedTheme?.submitButtonText || 'Sign & Submit'}
                                        </Button>
                                    </span>
                                </Tooltip>
                            ) : (
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    sx={{
                                        borderRadius: enrichedTheme?.submitButtonBorderRadius || '12px',
                                        textTransform: 'none', fontWeight: 700,
                                        bgcolor: enrichedTheme?.submitButtonColor || common.mintyZest,
                                        color: enrichedTheme?.submitButtonTextColor || '#fff',
                                        px: 6, py: 1.75,
                                        fontSize: '16px',
                                        letterSpacing: '-0.01em',
                                        boxShadow: `0 8px 16px ${alpha(enrichedTheme?.submitButtonColor || common.mintyZest, 0.24)}`,
                                        '&:hover': {
                                            bgcolor: enrichedTheme?.submitButtonColor || '#009c4e',
                                            boxShadow: `0 8px 24px ${alpha(enrichedTheme?.submitButtonColor || common.mintyZest, 0.36)}`,
                                        },
                                    }}
                                >
                                    {isSubmitting ? 'Submitting...' : (enrichedTheme?.submitButtonText || 'Sign & Submit')}
                                </Button>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Error snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    sx={{ borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
        </LocalizationProvider>
    );
};

ConsentFormPreview.propTypes = {
    data: PropTypes.shape({
        formData: PropTypes.object.isRequired,
        leadData: PropTypes.object,
        isDummy: PropTypes.bool.isRequired,
        practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
    onClose: PropTypes.func.isRequired,
};

// ── Loading overlay — shown while API fetches form + lead data ──

export const PreviewLoadingOverlay = ({ onClose }) => (
    <Box sx={{
        position: 'fixed', inset: 0, zIndex: 1400,
        bgcolor: '#f5f5f5',
        display: 'flex', flexDirection: 'column',
    }}>
        <Box sx={{
            bgcolor: '#fff', borderBottom: '1px solid', borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5,
        }}>
            <Button
                startIcon={<Iconify icon="eva:close-fill" width={20} />}
                onClick={onClose}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
            >
                Cancel
            </Button>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    Loading Preview...
                </Typography>
            </Box>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
            <ConsentLoader size={56} color={common.mintyZest} />
            <Typography variant="body2" color="text.secondary">
                Preparing consent form preview...
            </Typography>
        </Box>
    </Box>
);

PreviewLoadingOverlay.propTypes = {
    onClose: PropTypes.func.isRequired,
};

export default ConsentFormPreview;
